import { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../api/reportsApi';
import type {
  DateRange,
  ReportsSummaryResponse,
  RequestsOverTimeResponse,
  DeliveryBreakdownResponse,
  FormTypeUsageResponse,
  PeakUsageResponse,
  TopAgentsResponse,
  StatusDistributionResponse,
  UserTypeBreakdownResponse,
} from '../types/reports';

export type SectionKey =
  | 'summary'
  | 'requestsOverTime'
  | 'deliveryBreakdown'
  | 'formTypeUsage'
  | 'peakUsage'
  | 'topAgents'
  | 'statusDistribution'
  | 'userTypeBreakdown';

export type ReportsDashboardState = {
  summary:            ReportsSummaryResponse | null;
  requestsOverTime:   RequestsOverTimeResponse | null;
  deliveryBreakdown:  DeliveryBreakdownResponse | null;
  formTypeUsage:      FormTypeUsageResponse | null;
  peakUsage:          PeakUsageResponse | null;
  topAgents:          TopAgentsResponse | null;
  statusDistribution: StatusDistributionResponse | null;
  userTypeBreakdown:  UserTypeBreakdownResponse | null;
};

type LoadingState = Record<SectionKey, boolean>;
type ErrorState   = Record<SectionKey, string | null>;

const INITIAL_DATA: ReportsDashboardState = {
  summary: null, requestsOverTime: null, deliveryBreakdown: null,
  formTypeUsage: null, peakUsage: null, topAgents: null,
  statusDistribution: null, userTypeBreakdown: null,
};

const ALL_KEYS: SectionKey[] = Object.keys(INITIAL_DATA) as SectionKey[];

function initLoading(val: boolean): LoadingState {
  return Object.fromEntries(ALL_KEYS.map((k) => [k, val])) as LoadingState;
}

function initErrors(): ErrorState {
  return Object.fromEntries(ALL_KEYS.map((k) => [k, null])) as ErrorState;
}

export function useReportsDashboard(
  dateRange: DateRange,
  dateFrom?: string,
  dateTo?: string,
) {
  const [data, setData]       = useState<ReportsDashboardState>(INITIAL_DATA);
  const [loading, setLoading] = useState<LoadingState>(initLoading(true));
  const [errors, setErrors]   = useState<ErrorState>(initErrors());

  const setSection = useCallback(<K extends SectionKey>(
    key: K,
    value: ReportsDashboardState[K] | null,
    error?: string,
  ) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setLoading((prev) => ({ ...prev, [key]: false }));
    setErrors((prev) => ({ ...prev, [key]: error ?? null }));
  }, []);

  const fetch = useCallback((signal: AbortSignal) => {
    // For custom range, skip fetch if dates are not yet set
    if (dateRange === 'custom' && (!dateFrom || !dateTo)) {
      setLoading(initLoading(false));
      setErrors(initErrors());
      return;
    }

    setLoading(initLoading(true));
    setErrors(initErrors());

    const df = dateFrom;
    const dt = dateTo;

    reportsApi.getSummary(dateRange, signal, df, dt)
      .then((d) => setSection('summary', d))
      .catch((e) => { if (!signal.aborted) setSection('summary', null, e.message); });

    reportsApi.getRequestsOverTime(dateRange, signal, df, dt)
      .then((d) => setSection('requestsOverTime', d))
      .catch((e) => { if (!signal.aborted) setSection('requestsOverTime', null, e.message); });

    reportsApi.getDeliveryBreakdown(dateRange, signal, df, dt)
      .then((d) => setSection('deliveryBreakdown', d))
      .catch((e) => { if (!signal.aborted) setSection('deliveryBreakdown', null, e.message); });

    reportsApi.getFormTypeUsage(dateRange, signal, df, dt)
      .then((d) => setSection('formTypeUsage', d))
      .catch((e) => { if (!signal.aborted) setSection('formTypeUsage', null, e.message); });

    reportsApi.getPeakUsage(dateRange, signal, df, dt)
      .then((d) => setSection('peakUsage', d))
      .catch((e) => { if (!signal.aborted) setSection('peakUsage', null, e.message); });

    reportsApi.getTopAgents(dateRange, signal, df, dt)
      .then((d) => setSection('topAgents', d))
      .catch((e) => { if (!signal.aborted) setSection('topAgents', null, e.message); });

    reportsApi.getStatusDistribution(dateRange, signal, df, dt)
      .then((d) => setSection('statusDistribution', d))
      .catch((e) => { if (!signal.aborted) setSection('statusDistribution', null, e.message); });

    reportsApi.getUserTypeBreakdown('sent', dateRange, signal, df, dt)
      .then((d) => setSection('userTypeBreakdown', d))
      .catch((e) => { if (!signal.aborted) setSection('userTypeBreakdown', null, e.message); });

  }, [dateRange, dateFrom, dateTo, setSection]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(controller.signal);
    return () => controller.abort();
  }, [fetch]);

  const refetch = useCallback(() => {
    const controller = new AbortController();
    fetch(controller.signal);
  }, [fetch]);

  return { data, loading, errors, refetch };
}
